import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, FileText, CheckCircle, XCircle, Clock, Paperclip } from "react-feather";
import AttachmentPreviewModal from "../../../../components/common/AttachmentPreviewModal";
import DataTable from "../../../../components/common/DataTable";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import { usePendingVehicleRequests } from "../usePendingVehicleRequests";
import VehicleRequestDecisionModal from "../components/VehicleRequestDecisionModal";
import {
  useListMaintenanceRequestsQuery,
  useApproveMaintenanceRequestMutation,
  useRejectMaintenanceRequestMutation,
} from "../../../masters/vehicles/vehiclesApi";
import { useCanApproveVehicleRequests } from "../../../masters/vehicles/useVehiclePermissions";
import { useCanCreateLpo } from "../../useStockPermissions";
import { useGetMaintenanceRequestQuery, useLazyGetMaintenanceRequestAttachmentContentQuery } from "../../../masters/vehicles/vehiclesApi";
import VehicleRequestDetailCard from "../../../masters/vehicles/components/VehicleRequestDetailCard";
import { paths } from "../../../../routes/paths";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";

const money = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const asDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");

// A row opens into the full request - work, spares, cash, and its attachment (2026-09-23:
// "the attachments should be visible ... even when converting to lpo").
const ExpandedRequest = ({ recordGuid }) => {
  const { data, isError } = useGetMaintenanceRequestQuery(recordGuid);
  if (isError) return <p className="text-danger mb-0">Could not load this request.</p>;
  return <div className="p-2"><VehicleRequestDetailCard request={data} /></div>;
};


// Two different "pending" states live on this page, and conflating them is what
// made two submitted requests look lost (reported 2026-09-10 - "not showing in
// react pending requests"):
//
//   1. AWAITING APPROVAL (SUBMITTED) - a manager has to decide. Added here
//      2026-09-10 on request ("so the managers can approve from react itself");
//      the approve/reject endpoints have existed since Batch40, only the screen
//      was missing, so approving previously meant opening the Flutter app.
//   2. APPROVED, NO LPO YET - already decided, now waiting for someone to raise
//      the purchase order. This is what the page originally showed, and its
//      filter (approved + has spare items + no active LPO) lives in
//      usePendingVehicleRequests.js, shared with LpoForm's own picker.
//
// A cash-only request (no spare items) legitimately never reaches list 2 - it
// becomes a petty cash voucher on approval and there is nothing to purchase.
const PendingVehicleRequests = () => {
  const navigate = useNavigate();
  const canApprove = useCanApproveVehicleRequests();
  const canRaiseLpo = useCanCreateLpo();
  // Both lists follow the header branch (2026-10-02); null = every branch the caller has.
  const { selectedBranchId } = useBranchFilter();

  // Skipped entirely for an approver who cannot raise an LPO - it would be two
  // wasted requests (requests + LPOs) for a section they will not be shown.
  const { pendingRequests: approvedRows, isLoading } = usePendingVehicleRequests(!canRaiseLpo, selectedBranchId);

  // Separate query, deliberately not folded into usePendingVehicleRequests:
  // that hook also feeds LpoForm's "approved request" picker, which must keep
  // returning approved-only.
  const { data: submittedPage, isLoading: isLoadingSubmitted } = useListMaintenanceRequestsQuery(
    { status: "SUBMITTED", pageSize: 200, branchId: selectedBranchId ?? undefined },
    { skip: !canApprove }
  );
  const submittedRows = submittedPage?.items || [];

  const [approveRequest, { isLoading: isApproving }] = useApproveMaintenanceRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] = useRejectMaintenanceRequestMutation();
  const [decision, setDecision] = useState({ show: false, mode: "approve", request: null });

  const handleConvert = (request) => {
    navigate(paths.stockLpoNew, {
      state: { fromVehicleRequest: { recordGuid: request.recordGuid, requestNumber: request.requestNumber } },
    });
  };

  // "The decision column should have the option to view attachment" (2026-09-23): the
  // request's file (odometer photo / invoice / PDF) opens in a preview from the row
  // itself, before Approve / Reject or Convert - no need to expand the row.
  const [fetchAttachment] = useLazyGetMaintenanceRequestAttachmentContentQuery();
  const [attachmentPreview, setAttachmentPreview] = useState(null); // { blob, request }
  const handleViewAttachment = async (request) => {
    try {
      const blob = await fetchAttachment(request.recordGuid, true).unwrap();
      setAttachmentPreview({ blob, request });
    } catch (error) {
      toast.error(error?.message || "Could not open the attachment");
    }
  };
  const attachmentButton = (record) =>
    record.hasAttachment ? (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
        title={record.attachmentFileName ? `View ${record.attachmentFileName}` : "View attachment"}
        onClick={() => handleViewAttachment(record)}
      >
        <Paperclip size={14} /> Attachment
      </button>
    ) : null;

  const handleDecision = async (payload) => {
    const { mode, request } = decision;
    try {
      if (mode === "approve") {
        const approved = await approveRequest({ recordGuid: request.recordGuid, ...payload }).unwrap();
        // Say a voucher was raised only when the server really raised one - it
        // ignores the amount on a request with no cash remarks (fixed 2026-09-19).
        toast.success(
          approved?.pettyCashVoucherRecordGuid
            ? `${request.requestNumber} approved - a petty cash voucher for ${money(approved.approvedCashAmount)} was raised and is waiting in Petty Cash.`
            : `${request.requestNumber} approved. No petty cash voucher was raised.`
        );
      } else {
        await rejectRequest({ recordGuid: request.recordGuid, ...payload }).unwrap();
        toast.success(`${request.requestNumber} rejected.`);
      }
      setDecision({ show: false, mode: "approve", request: null });
    } catch (err) {
      toast.error(err?.data?.message || "Could not save that decision.");
    }
  };

  // The vehicle leads, the request number sits under it (2026-09-12).
  const vehicleCell = (v, r) => (
    <PrimaryCell
      primary={v ? `${v}${r.brand ? ` (${r.brand} ${r.model || ""})` : ""}`.trim() : null}
      secondary={r.requestNumber}
    />
  );

  const submittedColumns = [
    { title: "Vehicle", dataIndex: "regNo", render: vehicleCell },
    { title: "Requested By", dataIndex: "requestedByName", render: (v) => v || "-" },
    { title: "Date", dataIndex: "requestDate", render: asDate },
    { title: "Work", dataIndex: "serviceRepairDescription", render: (v) => v || "-" },
    {
      title: "Spares",
      dataIndex: "spareItemCount",
      align: "right",
      render: (v) => (v > 0 ? v : <span className="text-muted">none</span>),
    },
    {
      title: "Cash asked",
      dataIndex: "requestedCashAmount",
      align: "right",
      render: (v) => (v == null ? <span className="text-muted">-</span> : money(v)),
    },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Decision",
      dataIndex: "decision",
      render: (_, record) => (
        <div className="d-flex gap-2 flex-wrap">
          {attachmentButton(record)}
          <button
            type="button"
            className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
            onClick={() => setDecision({ show: true, mode: "approve", request: record })}
          >
            <CheckCircle size={14} /> Approve
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
            onClick={() => setDecision({ show: true, mode: "reject", request: record })}
          >
            <XCircle size={14} /> Reject
          </button>
        </div>
      ),
    },
  ];

  const approvedColumns = [
    { title: "Vehicle", dataIndex: "regNo", render: vehicleCell },
    { title: "Requested By", dataIndex: "requestedByName" },
    { title: "Date", dataIndex: "requestDate", render: asDate },
    { title: "Spare Supplier", dataIndex: "spareSupplierName", render: (v) => v || "-" },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="d-flex gap-2 flex-wrap">
          {attachmentButton(record)}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => handleConvert(record)}
          >
            <FileText size={14} /> Convert to LPO
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>Pending Vehicle Requests</h4>
              <h6>Requests waiting for a decision, and approved ones waiting to become an LPO</h6>
            </div>
          </div>
        </div>

        {canApprove && (
          <div className="card table-list-card mb-4">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Clock size={16} className="text-warning" />
                <h5 className="mb-0">Awaiting approval</h5>
                {submittedRows.length > 0 && (
                  <span className="badge badge-linewarning">{submittedRows.length}</span>
                )}
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={submittedColumns}
                  dataSource={submittedRows}
                  rowKey="recordGuid"
                  loading={isLoadingSubmitted}
                  expandable={{ expandedRowRender: (r) => <ExpandedRequest recordGuid={r.recordGuid} /> }}
                />
              </div>
              {!isLoadingSubmitted && submittedRows.length === 0 && (
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Nothing is waiting for a decision.
                </p>
              )}
            </div>
          </div>
        )}

        {canRaiseLpo && (
          <div className="card table-list-card">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-3">
                <FileText size={16} className="text-primary" />
                <h5 className="mb-0">Approved, waiting for an LPO</h5>
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={approvedColumns}
                  dataSource={approvedRows}
                  rowKey="recordGuid"
                  loading={isLoading}
                  expandable={{ expandedRowRender: (r) => <ExpandedRequest recordGuid={r.recordGuid} /> }}
                />
              </div>
              <small className="text-muted d-block mt-2">
                Only approved requests that list spare items appear here - a cash-only request is
                settled by its petty cash voucher and has nothing to purchase.
              </small>
            </div>
          </div>
        )}
      <AttachmentPreviewModal
        show={Boolean(attachmentPreview)}
        onClose={() => setAttachmentPreview(null)}
        blob={attachmentPreview?.blob}
        fileName={attachmentPreview?.request?.attachmentFileName}
        title={attachmentPreview?.request ? `${attachmentPreview.request.requestNumber} - attachment` : "Attachment"}
      />

        <VehicleRequestDecisionModal
          show={decision.show}
          mode={decision.mode}
          request={decision.request}
          isSaving={isApproving || isRejecting}
          onClose={() => setDecision({ show: false, mode: "approve", request: null })}
          onConfirm={handleDecision}
        />
      </div>
    </div>
  );
};

export default PendingVehicleRequests;
